import { useRSOList } from "@/api/services/admin/rso";
import { TGetRSOTypeDto } from "@/api/services/admin/rso/dtos/get-match-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { Table, TableSorting } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { ERSOContractStatus } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { Box } from "@mui/material";
import { formatRSOId } from "@/utils/number";
import { formatDate, formatDateTime } from "@/utils/datetime";

interface PartnerTableProps {
  contractStatuses: ERSOContractStatus[];
}

export const PartnerTable: FC<PartnerTableProps> = ({ contractStatuses }) => {
  const { data: matchContractStatuses } = useEnum("ERSOContractStatus");
  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<TGetRSOTypeDto>[]>(() => {
    // Get the current contract status (first one in the array)
    const currentContractStatus = contractStatuses?.[0];

    // Determine column header and field based on contract status
    const getDateColumnConfig = () => {
      switch (currentContractStatus) {
        case ERSOContractStatus.DURING_CONTRACT:
          return {
            header: "契約開始日",
            field: "contractStartDate",
            sortName: "rso.contractStartDate",
          };
        case ERSOContractStatus.CANCELED_CONTRACT:
          return {
            header: "解約日",
            field: "contractEndDate",
            sortName: "rso.contractEndDate",
          };
        case ERSOContractStatus.TRIAL:
          return {
            header: "トライアル開始日",
            field: "trialEndDate",
            sortName: "rso.trialEndDate",
          };
        case ERSOContractStatus.TRIAL_CANCELLED:
          return {
            header: "トライアル終了日",
            field: "trialStartDate",
            sortName: "rso.trialStartDate",
          };
        default:
          return {
            header: "登録日",
            field: "createdAt",
            sortName: "rso.createdAt",
          };
      }
    };

    const dateColumnConfig = getDateColumnConfig();
    const defaultDateColumnConfig = {
      header: "登録日",
      field: "createdAt",
      sortName: "rso.createdAt",
    };

    return [
      {
        header: "組織ID",
        id: "id",
        accessorKey: "id",
        Cell: ({ row }) => {
          return <Box sx={{ pl: 2 }}>{formatRSOId(row.original.id)}</Box>;
        },
        Header: (
          <Box sx={{ pl: 2 }}>
            <TableSorting name="rso.id">組織ID</TableSorting>
          </Box>
        ),
        size: 120,
      },
      {
        header: "登録支援機関名",
        id: "name",
        accessorKey: "name",
        Header: <TableSorting name="rso.name">登録支援機関名</TableSorting>,
        Cell: ({ row }) => truncateWithEllipsis(row.original.name, 25),
        size: 157,
      },
      {
        header: "課金対象数",
        id: "currentMonthBillingCount",
        accessorKey: "currentMonthBillingCount",
        Cell: ({ row }) => row.original?.currentMonthBillingCount ?? 0,
        size: 102,
      },
      {
        header: "前月課金数",
        id: "previousMonthBillingCount",
        accessorKey: "previousMonthBillingCount",
        Cell: ({ row }) => row.original?.previousMonthBillingCount ?? 0,
        size: 102,
      },
      {
        header: "契約プラン",
        id: "contractPlan",
        accessorKey: "contractPlan",
        Header: <TableSorting name="rso.contractPlan">契約プラン</TableSorting>,
        Cell: ({ row }) => truncateWithEllipsis(row.original.contractPlan ?? "-", 50),
        size: 60,
      },
      {
        header: "外国人数",
        Cell: ({ row }) => {
          return row.original.currentLearnerCount ?? 0;
        },
        size: 120,
      },
      {
        header: "有効アカウント数",
        Cell: ({ row }) => {
          return row.original?.activeAccountLearnerCount ?? 0;
        },
        size: 120,
      },
      {
        header: dateColumnConfig.header,
        Cell: ({ row }) => formatDate(row.original[dateColumnConfig.field as keyof TGetRSOTypeDto] as string | null | undefined),
        Header: <TableSorting name={dateColumnConfig.sortName}>{dateColumnConfig.header}</TableSorting>,
        size: 20,
      },
      {
        header: defaultDateColumnConfig.header,
        Cell: ({ row }) => formatDate(row.original[defaultDateColumnConfig.field as keyof TGetRSOTypeDto] as string | null | undefined),
        Header: <TableSorting name={defaultDateColumnConfig.sortName}>{defaultDateColumnConfig.header}</TableSorting>,
        size: 20,
      },
      {
        header: "最終ログイン日時",
        id: "lastLoginTime",
        accessorKey: "lastLoginTime",
        Cell: ({ row }) => formatDateTime(row.original.lastLoginTime),
        size: 120,
      },
    ];
  }, [matchContractStatuses, contractStatuses]);

  const { data, isLoading } = useRSOList(contractStatuses);
  const [heightTable] = useState<string>("calc(100vh - 430px)");

  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    mutedRow: (row) => !!row.deletedAt,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.rsoDetail, { id: row.id })),
    pinningColumns: ["action"],
    renderEmptyRowsFallback: ({}) => <span></span>,
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <Box mt={1}>
        <NewPagination totalPage={data?.pagination.totalPage} />
      </Box>
    </>
  );
};
